/**
 * Career Service
 * Handles fetching and managing career data from the HR database
 */

export interface Career {
  id: number;
  title: string;
  department: string;
  location: string;
  employment_type: string;
  status: 'new' | 'closed';
  description?: string;
  requirements?: string;
  responsibilities?: string;
  qualifications?: string;
  benefits?: string;
  salary_range?: string;
  created_at: string;
  updated_at: string;
}

export interface CareerRoute {
  id: number;
  title: string;
  slug: string;
  department: string;
  applyRoute: string;
  detailRoute: string;
}

export class CareerService {
  private static baseUrl = '/api/careers';

  /**
   * Generate URL slug from job title
   */
  static generateSlug(title: string): string {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  }

  /**
   * Get all active job postings
   */
  static async getActiveCareers(): Promise<Career[]> {
    try {
      const response = await fetch(`${this.baseUrl}/active`);
      if (!response.ok) {
        throw new Error('Failed to fetch active careers');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching active careers:', error);
      // Return fallback data if API fails
      return this.getFallbackCareers();
    }
  }

  /**
   * Get all job postings (including closed ones)
   */
  static async getAllCareers(): Promise<Career[]> {
    try {
      const response = await fetch(`${this.baseUrl}`);
      if (!response.ok) {
        throw new Error('Failed to fetch careers');
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching careers:', error);
      return this.getFallbackCareers();
    }
  }

  /**
   * Get a specific job posting by ID
   */
  static async getCareerById(id: number): Promise<Career | null> {
    try {
      const response = await fetch(`${this.baseUrl}/${id}`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching career by ID:', error);
      return null;
    }
  }

  /**
   * Get a specific job posting by slug
   */
  static async getCareerBySlug(slug: string): Promise<Career | null> {
    try {
      const response = await fetch(`${this.baseUrl}/slug/${slug}`);
      if (!response.ok) {
        return null;
      }
      return await response.json();
    } catch (error) {
      console.error('Error fetching career by slug:', error);
      return null;
    }
  }

  /**
   * Get career routes for dynamic routing
   */
  static async getCareerRoutes(): Promise<CareerRoute[]> {
    const careers = await this.getActiveCareers();
    return careers.map(career => ({
      id: career.id,
      title: career.title,
      slug: this.generateSlug(career.title),
      department: career.department,
      applyRoute: `/careers/apply/${this.generateSlug(career.title)}`,
      detailRoute: `/careers/${this.generateSlug(career.title)}`
    }));
  }

  /**
   * Find career by slug from routes
   */
  static async findCareerBySlug(slug: string): Promise<Career | null> {
    const routes = await this.getCareerRoutes();
    const route = routes.find(r => r.slug === slug);
    if (!route) return null;
    return await this.getCareerById(route.id);
  }

  /**
   * Get job title from URL slug
   */
  static async getJobTitleFromSlug(slug: string): Promise<string> {
    const career = await this.findCareerBySlug(slug);
    return career?.title || 'Job Application';
  }

  /**
   * Fallback data when API is not available
   */
  private static getFallbackCareers(): Career[] {
    return [
      {
        id: 1,
        title: 'Senior AI Engineer (Agentic AI & Multimodal Systems)',
        department: 'Engineering',
        location: 'Remote (U.S. or Global)',
        employment_type: 'Full-time',
        status: 'new',
        description: 'We are seeking a Senior AI Engineer to lead the development of our Agentic AI platform, focusing on multimodal systems that integrate vision, language, and reasoning capabilities.',
        requirements: '5+ years of experience in AI/ML engineering, expertise in Python, PyTorch/TensorFlow, experience with large language models, multimodal AI systems, and agentic AI architectures.',
        responsibilities: 'Design and implement agentic AI systems, develop multimodal AI capabilities, lead technical architecture decisions, mentor junior engineers, collaborate with product teams.',
        qualifications: 'PhD or MS in Computer Science, AI, or related field. Strong background in machine learning, natural language processing, computer vision, and distributed systems.',
        benefits: 'Competitive salary, equity participation, comprehensive health benefits, flexible work arrangements, professional development budget.',
        salary_range: '$120,000 - $180,000',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 2,
        title: 'Clinical Product Manager (Agentic AI Healthcare Platform)',
        department: 'Product',
        location: 'Remote (U.S. or Global)',
        employment_type: 'Full-time',
        status: 'new',
        description: 'We are seeking a Clinical Product Manager to bridge clinical expertise with AI-driven product innovation in healthcare.',
        requirements: '5+ years in clinical informatics or healthcare product management, clinical background (MD, RN, NP, PA, PharmD), experience with EHR systems, AI/ML products.',
        responsibilities: 'Translate clinical workflows into product requirements, collaborate with AI engineers, define use cases for Agentic AI modules, ensure compliance with healthcare standards.',
        qualifications: 'Clinical degree or equivalent experience, strong understanding of care delivery workflows, experience with Epic, Cerner, AthenaHealth, familiarity with FHIR, HL7 standards.',
        benefits: 'Competitive salary, equity participation, comprehensive health benefits, flexible work arrangements, clinical education budget.',
        salary_range: '$100,000 - $150,000',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      },
      {
        id: 3,
        title: 'Full-Stack Developer (React / GraphQL / IoT / UX-Focused)',
        department: 'Engineering',
        location: 'Remote (U.S. or Global)',
        employment_type: 'Full-time',
        status: 'new',
        description: 'We are seeking a Full-Stack Developer with strong expertise in React, GraphQL, Next.js, and TypeScript for our healthcare platform.',
        requirements: '5+ years full-stack experience, expert in React/Next.js/GraphQL/TypeScript, familiarity with Rust, understanding of Agentic AI concepts, IoT integration experience.',
        responsibilities: 'Design responsive web/mobile interfaces, build GraphQL APIs, integrate with AI workflows, develop IoT device connections, implement real-time communication.',
        qualifications: 'Expert in React, Next.js, GraphQL, TypeScript, familiarity with Rust, experience with IoT integrations (BLE, MQTT, WebRTC), UX/UI design systems knowledge.',
        benefits: 'Competitive salary, equity participation, comprehensive health benefits, flexible work arrangements, professional development budget.',
        salary_range: '$90,000 - $140,000',
        created_at: new Date().toISOString(),
        updated_at: new Date().toISOString()
      }
    ];
  }
}
